import {AfterViewInit, Component, OnInit, QueryList, ViewChild, ViewChildren} from '@angular/core';
import {MatPaginator} from "@angular/material/paginator";
import {MatSort} from "@angular/material/sort";
import {FilterBuilder, FilterComponent} from "@core/shared/models/filter";
import {Society} from "@core/shared/models/society";
import {User} from "@core/shared/models/user";
import {SelectionModel} from "@angular/cdk/collections";
import {LOCALE_ITEMS, LocaleItem} from "@core/shared/models/translation";
import {ActivatedRoute, Router} from "@angular/router";
import {MatDialog, MatDialogRef} from "@angular/material/dialog";
import {TranslateService} from "@ngx-translate/core";
import {MatSnackBar} from "@angular/material/snack-bar";
import {UserService} from "@core/shared/services/user.service";
import {merge} from "rxjs";
import {map, startWith, switchMap} from "rxjs/operators";
import {ConfirmDialogComponent} from "@lib/confirm-dialog/confirm-dialog.component";
import {DEFAULT_PAGE_SIZE, DEFAULT_PAGE_SIZE_OPTIONS, Pagination} from "@core/shared/models/pagination";
import {Access} from "@core/shared/models/access";
import {OfferPermanentOption} from "@core/shared/models/offer/offer-permanent-option";
import {OfferPermanentOptionService} from "@core/shared/services/offer/offer-permanent-option.service";
import {TranslationService} from "@core/shared/services/translation.service";
import {PRICE_AREAS, PRICE_TYPES, PriceArea, PriceType, PRICING_TYPES, PricingType} from "@core/shared/models/price";
import {CamelizePipe} from 'ngx-pipes';

@Component({
    selector: 'app-core-page-offer-permanent-option-list',
    templateUrl: './page-offer-permanent-option-list.component.html',
    styleUrls: ['./page-offer-permanent-option-list.component.scss'],
    providers: [
        CamelizePipe
    ],
    standalone: false
})
export class PageOfferPermanentOptionListComponent implements OnInit, AfterViewInit {

    @ViewChild(MatPaginator) paginator: MatPaginator;

    @ViewChild(MatSort) sort: MatSort;

    @ViewChildren("filter") filterComponents: QueryList<FilterComponent>;

    public society: Society;

    public totalItems: number = 0;

    public user: User;

    public displayedColumns: string[] = [
        'actions',
        'reference',
        'translations.title',
        'providerName',
        'price.priceArea',
        'price.pricingType',
        'price.priceType',
        'startFromPriceTTC',
        'totalOffers',
        'locales'
    ];

    public filterBuilder: FilterBuilder;

    public items: OfferPermanentOption[] = [];

    public selection = new SelectionModel<OfferPermanentOption>(true, []);

    public locales: LocaleItem[] = [];

    public priceAreas: { id: PriceArea, label: string }[] = [];

    public pricingTypes: { id: PricingType, label: string }[] = [];

    public priceTypes: { id: PriceType, label: string }[] = [];

    constructor(
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        private _dialog: MatDialog,
        private _translateService: TranslateService,
        private _snackBar: MatSnackBar,
        private _offerPermanentOptionService: OfferPermanentOptionService,
        private _userService: UserService,
        private _camelizePipe: CamelizePipe,
        public translationService: TranslationService
    ) {
    }

    ngOnInit() {

        this.user = this._userService.currentUser.value;

        this._activatedRoute.data.subscribe((data: { society: Society }): void => {

            this.society = data.society;

            this._initLocales();

            this._initPriceAreas();

            this._initPricingTypes();

            this._initPriceTypes();

            this._initFilterBuilder();
        });
    }

    ngAfterViewInit(): void {

        this.sort.sortChange.subscribe(() => {

            this._resetPagination();
        });

        merge(this.sort.sortChange, this.paginator.page)
            .pipe(
                startWith({}),
                switchMap(() => {

                    return this._offerPermanentOptionService.getPaginationItemsAPI(this.society.id, this.itemsApiParams);

                }),
                map(this.mapApiResult),
            ).subscribe((data: OfferPermanentOption[]): void => {

                this.items = data;

                this.selection.clear();
            }
        );
    }

    private _initLocales(): void {

        this.locales = LOCALE_ITEMS.map((item: LocaleItem): LocaleItem => {

            return {
                id: item.id,
                label: this._translateService.instant(item.label)
            };
        });
    }

    private _initPriceAreas(): void {

        this.priceAreas = PRICE_AREAS.map((item: PriceArea): { id: PriceArea, label: string } => {

            return {
                id: item,
                label: `priceArea.${this._camelizePipe.transform(this._translateService.instant(item))}.value`
            };
        });
    }

    private _initPricingTypes(): void {

        this.pricingTypes = PRICING_TYPES.map((item: PricingType): { id: PricingType, label: string } => {

            return {
                id: item,
                label: `offer.permanentOption.pricingType.${this._camelizePipe.transform(this._translateService.instant(item))}.value`
            };
        });
    }

    private _initPriceTypes(): void {

        this.priceTypes = PRICE_TYPES.map((item: PriceType): { id: PriceType, label: string } => {

            return {
                id: item,
                label: `priceType.${this._camelizePipe.transform(this._translateService.instant(item))}.value`
            };
        });
    }

    private _initFilterBuilder(): void {

        this.filterBuilder = new FilterBuilder();

        this.filterBuilder.filterCallback = (): void => {

            this._resetPagination();

            this._offerPermanentOptionService.getPaginationItemsAPI(this.society.id, this.itemsApiParams).pipe(map(this.mapApiResult)).subscribe((data: OfferPermanentOption[]): void => {

                this.items = data;
            });
        };
    }

    private _resetPagination(): void {

        this.paginator.pageIndex = 0;
    }

    public resetFilters(): void {

        this.filterBuilder.resetFields();

        this.filterComponents.forEach((filterComponent: FilterComponent): void => {

            filterComponent.reset();
        });

        this._resetPagination();

        this._offerPermanentOptionService.getPaginationItemsAPI(this.society.id, this.itemsApiParams).pipe(map(this.mapApiResult)).subscribe((data: OfferPermanentOption[]): void => {

            this.items = data;
        });
    }

    public getModel(item: OfferPermanentOption): OfferPermanentOption {

        return item;
    }

    public getFilterColumnDef(value: string): string {

        return `${value}-filter`;
    }

    public openDeleteItemDialog(id: number): void {

        const title : string = this._translateService.instant('offer.permanentOption.delete.value');

        const content : string = `<p>${this._translateService.instant('offer.permanentOption.delete.content.value')}</p>`;

        const dialogRef: MatDialogRef<ConfirmDialogComponent> = this._dialog.open(ConfirmDialogComponent, {
            width: '500px',
            data: {
                title: title,
                content: content
            }
        });

        dialogRef.componentInstance.confirm.subscribe((): void => {

            this._offerPermanentOptionService.deleteItemAPI(id).subscribe((): void => {

                this._snackBar.open(this._translateService.instant('offer.permanentOption.delete.success.value'), this._translateService.instant('notification.close.action.value'), {
                    duration: 5000
                });

                this._resetPagination();

                this._offerPermanentOptionService.getPaginationItemsAPI(this.society.id, this.itemsApiParams).pipe(map(this.mapApiResult)).subscribe((data: OfferPermanentOption[]): void => {

                    this.items = data;
                });
            });
        });
    }

    public addItem(): void {

        this._router.navigate(['account/offer/permanent-option/create']);
    }

    public editItem(id: number): void{

        this._router.navigate(['account/offer/permanent-option/update', id]);
    }

    public getParsedLocales(item: OfferPermanentOption): string {

        return item.locales.map((locale: string): string => {

            return this._translateService.instant(`locale.${locale}.value`);

        }).join(', ');
    }

    public hasAccessCreate(): boolean {

        const accesses = this.user.accesses.filter((access : Access) => {

            return access.tag == 'OFFER_PERMANENT_OPTION_CREATE_IS_MINE';
        });

        return (accesses.length > 0)
    }

    public hasAccessEdit(): boolean {

        const accesses = this.user.accesses.filter((access : Access) => {

            return access.tag == 'OFFER_PERMANENT_OPTION_EDIT_IS_MINE';
        });

        return (accesses.length > 0)
    }

    public hasAccessDelete(): boolean {

        const accesses = this.user.accesses.filter((access : Access) => {

            return access.tag == 'OFFER_PERMANENT_OPTION_DELETE_IS_MINE';
        });

        return (accesses.length > 0)
    }

    get itemsApiParams(): string[] {

        const params: string[] = [
            `page=${this.paginator.pageIndex + 1}`,
            `limit=${this.paginator.pageSize}`
        ];

        if(this.sort.active && this.sort.direction !== ''){

            params.push(`sort[${this.sort.active}]=${this.sort.direction.toUpperCase()}`);
        }

        if(this.filterBuilder.hasFilters){

            params.push(this.filterBuilder.serializedRequest);
        }

        return params;
    }

    get displayedFilterColumns(): string[] {

        return this.displayedColumns.map((column: string): string => {

            return this.getFilterColumnDef(column);
        });
    }

    get mapApiResult(): (data: Pagination<OfferPermanentOption>) => OfferPermanentOption[] {

        return (data: Pagination<OfferPermanentOption>) => {

            this.totalItems = data.totalItems;

            return data.items;
        }
    }

    get pageSize(): number {

        return DEFAULT_PAGE_SIZE;
    }

    get pageSizeOptions(): number[] {

        return DEFAULT_PAGE_SIZE_OPTIONS;
    }

    get localeId(): string {

        return this._translateService.currentLang;
    }
}
